/**
 *
 * @Copyright 2024 UNLOCKIT DECENTRALIZATION, LDA
 * Development by VOID Software, SA
 *
 */

import {
    FormEventHandler, FunctionComponent, useEffect, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TranslationContext, withTranslationContext } from '../../controllers/TranslationContext';
import Button from '../../elements/Button';
import { ButtonVariant } from '../../../types/general';
import { FormField } from '../../elements/FormField';
import { AppRoute } from '../../../constants/routes';
import { HelpAndSupportContext, withHelpAndSupportContext } from '../../controllers/HelpAndSupportContext';
import { FormValidationError } from '../../../utils/validations';
import { preparePageTitle } from '../../../utils/route';
import { UploadDocumentCard } from '../../elements/UploadDocumentCard';
import { MAXIMUM_FILE_SIZE_MB } from '../../../constants/help';
import { LoadingCircles } from '../../elements/LoadingCircles';
import { ProgressBar } from '../../elements/ProgressBar';

interface OwnProps extends TranslationContext, HelpAndSupportContext { }

type RouterState = {
    referrer?: string;
    returnTo?: string;
}

const NewSupportTicketScreenBase: FunctionComponent<OwnProps> = (props: OwnProps) => {
    const { t, submitTicket } = props;
    const location = useLocation();
    const navigate = useNavigate();
    const referrer = (location.state as RouterState)?.referrer ?? (window.location?.origin + location.pathname);
    const returnTo = window.location?.origin + ((location.state as RouterState)?.returnTo ?? AppRoute.Index);
    const [fieldErrors, setFieldErrors] = useState<FormValidationError | null>();
    const [files, setFiles] = useState<File[]|undefined>(undefined);
    const [uploading, setUploading] = useState(false);
    const [uploadingProgress, setUploadingProgress] = useState<number>(0);
    
    const actionsDisabled = uploading;

    useEffect(() => {
        document.title = preparePageTitle(t('newSupportTicketScreen.title'));
    }, []);
    
    const onSubmit: FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        setUploading(true);

        submitTicket({
            problem: formData.get('problem')?.toString() ?? '',
            contextUrl: referrer,
            files,
        }, {
            onUploadProgress: (event) => {
                if (event.lengthComputable) {
                    const progress = Math.round((event.loaded * 100) / (event.total ?? 1));
                    setUploadingProgress(progress);
                }
            },
        }).then((res) => {
            setUploading(false);
            setUploadingProgress(0);
            const [, error] = res;

            if (error?.errors.length) {
                toast.error(error.errors?.[0]?.getMessageTranslated(t));
                return;
            }

            if (error?.fields && Object.keys(error?.fields ?? {}).length > 0) {
                setFieldErrors({
                    fields: error.fields,
                });
                return;
            }
            
            navigate(AppRoute.SupportTicketSubmitted, {
                state: location.state,
            });
        });
    };

    const onCancel = () => {
        navigate(returnTo);
    };

    const handleFileUpload = async (file: FileList|null|undefined) => {
        if (!file || file.length === 0) {
            setFiles(undefined);
            return;
        }

        const newFiles = Array.from(file);
        setFiles(newFiles);
    };

    return (
        <div className="new-support-ticket-screen">
            <h1>{t('newSupportTicketScreen.title')}</h1>
            <p>{t('newSupportTicketScreen.description')}</p>

            <form onSubmit={onSubmit}>
                <FormField name="problem" minLength={10} maxLength={1024} isTextArea rows={5} label={t('newSupportTicketScreen.problemLabel')} placeholder={t('newSupportTicketScreen.problemPlaceholder')} errors={fieldErrors} fullWidth disabled={actionsDisabled} />

                <UploadDocumentCard
                    document={{
                        id: 1,
                        title: t('newSupportTicketScreen.filesUploadTitle'),
                        description: t('newSupportTicketScreen.filesUploadDescription'),
                    }}
                    onUploadFileCallback={handleFileUpload}
                    accept="image/*,video/*,application/pdf"
                    disabled={actionsDisabled}
                    multiple
                    showFileNames
                />
                <p>
                    {t('newSupportTicketScreen.filesUploadHelper', {
                        maxSizeMB: MAXIMUM_FILE_SIZE_MB,
                    })}
                </p>
                
                <div className="new-support-ticket-screen__actions">
                    <Button disabled={actionsDisabled} onClick={onCancel} variant={ButtonVariant.Curved} extraClasses="secondary" title={t('newSupportTicketScreen.cancel')}>{t('newSupportTicketScreen.cancel')}</Button>
                    <Button disabled={actionsDisabled} variant={ButtonVariant.Curved} isSubmit extraClasses="primary" title={t('newSupportTicketScreen.cancel')}>{t('newSupportTicketScreen.submit')}</Button>
                </div>
                {uploading && (
                    <div className="new-support-ticket-screen__loading">
                        <ProgressBar progress={uploadingProgress} size="xs" variant="primary" testId="upload-progress" />
                        <LoadingCircles size="l" variant="primary" testId="loading-circles" />
                    </div>
                )}
            </form>
        </div>
    );
};

export const NewSupportTicketScreen = withHelpAndSupportContext(withTranslationContext(NewSupportTicketScreenBase));
